import { createContext, useContext, useState, useEffect } from "react";
import { postData } from "../api/apiService";
import { AUTH } from "../api/endpointMap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { clearPortalAccess, setUserVisiblePortals } from "../utils/portalAccess";
import { isPartnerSsoSession } from "../utils/embedFlow";
import { resetOnboardingStatusCache } from "../hooks/useOnboardingStatus";
import { resetSubscriptionStatusCache } from "../hooks/useSubscriptionStatus";

const AuthContext = createContext();

// JWT payload decoder. Surfaces the optional `impersonating` claim
// (shadow-token design: token.sub = admin's user id, token.impersonating =
// target user's id that the admin is acting as). Returns null on any parse
// failure — a malformed token should never crash the auth provider.
export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

// Where a role belongs after signing in. Partner staff land on the partner
// panel, super_admins on their admin workspace, tenant_admin / user on the
// portal picker. Unrecognised roles fall through to "/" so a new role can
// never lock anyone out. Exported because TWO callers must agree on this:
// the post-login redirect, and the guard that bounces an already-signed-in
// visitor away from /login.
export const landingPathForRole = (role) => {
  // Partner staff have no destination in this app any more: their portal moved
  // to the standalone NoticeAI Partner Portal (PARTNER_PORTAL_MERGE_PLAN.md).
  // They fall through to "/" like any unrecognised role rather than being sent
  // to a route that no longer exists.
  if (role === "super_admin") return "/admin";
  // Review User — org-less NABS staff; their whole world is the
  // Review Dashboard (managed-service queue), not the tenant app.
  if (role === "nabs_reviewer") return "/review-dashboard";
  return "/";
};

// True when a usable JWT is still on hand.
//
// The stored `user` object is NOT a session signal on its own: the 401
// handler removes `authToken` / `refreshToken` but deliberately leaves
// `user` in localStorage, so anything reading `user` would treat a dead
// session as live — bounce the visitor into the app, collect another 401,
// and land back on /login in a loop. The token, and its expiry, are the
// only honest evidence. A token that will not decode is treated as no
// session; a decodable one with no `exp` claim cannot have expired.
export const hasLiveSession = () => {
  const token = localStorage.getItem("authToken");
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (!payload.exp) return true;
  return payload.exp * 1000 > Date.now();
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isReadOnly, setIsReadOnly] = useState(() => {
    return localStorage.getItem("readOnlyMode") === "true";
  });
  const [isFirstPurchase, setIsFirstPurchase] = useState(false);
  // `impersonatingUserId` holds the target user id when this session is a
  // shadow impersonation token (JWT.impersonating claim). Null otherwise.
  const [impersonatingUserId, setImpersonatingUserId] = useState(() => {
    const token = localStorage.getItem("authToken");
    return decodeJwtPayload(token)?.impersonating || null;
  });

  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(user));
  }, [user]);

  useEffect(() => {
    if (isReadOnly) {
      document.body.classList.add("read-only-mode");
      document.documentElement.style.setProperty("--app-read-only", "true");
    } else {
      document.body.classList.remove("read-only-mode");
      document.documentElement.style.setProperty("--app-read-only", "false");
    }
  }, [isReadOnly]);

  const checkReadOnlyMode = () => {
    // Read-only can never apply to platform staff. The flag is written while
    // viewing the product AS a tenant (including impersonating one), and it
    // used to survive back into the admin's own session — leaving a
    // super_admin with an entirely unclickable admin panel, since the mode's
    // CSS disables every unexempted button and link. Their own account is
    // never read-only, so clear the leftover instead of honouring it.
    let storedRole = null;
    try {
      storedRole = JSON.parse(localStorage.getItem("user") || "null")?.role;
    } catch {
      /* malformed user blob — fall through to the normal check */
    }
    if (
      ["super_admin", "partner_admin", "partner_admin_user"].includes(
        storedRole
      )
    ) {
      localStorage.removeItem("readOnlyMode");
      setIsReadOnly(false);
      return;
    }
    if (localStorage.getItem("readOnlyMode") == "true") {
      setIsReadOnly(true);
    } else {
      setIsReadOnly(false);
    }
  };

  const checkIsFirstPurchase = () => {
    if (localStorage.getItem("isFirstPurchase") == "1") {
      setIsFirstPurchase(true);
    } else {
      setIsFirstPurchase(false);
    }
  };

  useEffect(() => {
    checkReadOnlyMode();
  }, []);

  const login = async (username, password, endpoint = AUTH.LOGIN) => {
    try {
      setLoading(true);
      const payload = {
        email: username,
        password: password,
      };
      let response;
      try {
        response = await postData(endpoint, payload);
      } catch (firstErr) {
        // Partner staff (role partner_admin) are rejected by the tenant
        // endpoint with the generic 401; retry them on the partner endpoint
        // so one sign-in page works for everyone. Any other outcome
        // (wrong password, suspended, disabled) surfaces the original error.
        if (endpoint !== AUTH.LOGIN) throw firstErr;
        try {
          response = await postData(AUTH.PARTNER_LOGIN, payload);
        } catch {
          throw firstErr;
        }
      }

      setLoading(false);
      if (response) {
        // FastAPI returns: { access_token, refresh_token, user_id, email, full_name, role, organization_id }
        const data = response.data || response;
        localStorage.setItem("authToken", data.access_token);
        localStorage.setItem("refreshToken", data.refresh_token);
        setUser({
          username: data.email,
          email: data.email,
          full_name: data.full_name,
          role: data.role,
          user_id: data.user_id,
          organization_id: data.organization_id,
          // Per-user portal flags — Feature 2.
          enable_income_tax_portal: data.enable_income_tax_portal,
          enable_gst_portal: data.enable_gst_portal,
          enable_tds_portal: data.enable_tds_portal,
          visible_portals: data.visible_portals || [],
          // Installation-wide Review Mode switch (System Configuration).
          // Hides every review-mode surface when false; the backend
          // gates the routes regardless.
          review_mode_enabled: data.review_mode_enabled ?? false,
          // Per-PORTAL capability flags (2026-07-15). Read by the portal-aware
          // useCapabilityEnabled() wrappers, which AND them with the org's plan
          // features. Undefined on a stale cached session falls back to the
          // columns' server-side default (RG/PA true, all-client false).
          enable_response_generation_income_tax:
            data.enable_response_generation_income_tax ?? true,
          enable_response_generation_gst:
            data.enable_response_generation_gst ?? true,
          enable_response_generation_tds:
            data.enable_response_generation_tds ?? true,
          enable_prepare_appeal_income_tax:
            data.enable_prepare_appeal_income_tax ?? true,
          enable_prepare_appeal_gst: data.enable_prepare_appeal_gst ?? true,
          enable_prepare_appeal_all_clients_income_tax:
            data.enable_prepare_appeal_all_clients_income_tax ?? false,
          enable_prepare_appeal_all_clients_gst:
            data.enable_prepare_appeal_all_clients_gst ?? false,
        });
        // Persist effective portal list so route guards (no React
        // context dependency) can read synchronously on first render.
        setUserVisiblePortals(data.visible_portals || []);
        // Drop module-level caches from any prior session so the new
        // user's onboarding state + subscription status are fetched
        // fresh instead of inherited from the previous account.
        // Without this, logging in as a fresh-signup user right after
        // a paid user logged out would skip the choose-path page
        // (stale has_chosen=true) and the navbar would still show the
        // previous user's trial pill.
        resetOnboardingStatusCache();
        resetSubscriptionStatusCache();
        setImpersonatingUserId(
          decodeJwtPayload(data.access_token)?.impersonating || null
        );
        // Role-based landing — see landingPathForRole above, shared with the
        // already-signed-in guard on /login so both agree.
        navigate(landingPathForRole(data.role));
        return true;
      }
    } catch (err) {
      setLoading(false);
      // Structured refusals ({code, message}, e.g. subscription_required for a
      // Direct API partner without a plan) show their message, not "[object Object]".
      const detail = err?.detail;
      toast.error(typeof err === "string" ? err
        : (detail && typeof detail === "object" ? detail.message : detail) || "Login failed");
      return false;
    }
  };

  const logout = async () => {
    // Capture embed-session state BEFORE the server call — the response
    // tells us whether to redirect (partner panel home), but the embed-
    // session check below depends on the in-memory JWT which we will
    // clear shortly. Caching the boolean keeps the post-clear branching
    // honest. `isPartnerSsoSession` reads the partner-SSO JWT claim
    // which is the only authoritative embed signal (partner_id alone
    // is NOT — partner_admin staff carry that too and they use the
    // regular UI).
    const wasPartnerSso = isPartnerSsoSession();

    // Best-effort server-side logout — invalidates refresh token + writes audit
    // row with IP/UA. The response now carries `redirect_url` populated from
    // the partner's `homepage_url` when the JWT carries `partner_id` AND the
    // partner is enabled AND has a homepage URL. Null otherwise.
    let redirectUrl = null;
    try {
      const resp = await postData(AUTH.LOGOUT, {});
      // postData unwraps differently across endpoints; try both shapes.
      redirectUrl = resp?.data?.redirect_url ?? resp?.redirect_url ?? null;
    } catch (err) {
      // Token may already be expired / server may be down — proceed with clear.
    }
    localStorage.removeItem("readOnlyMode");
    setUser(null);
    setImpersonatingUserId(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    sessionStorage.removeItem("impersonator_original_token");
    sessionStorage.removeItem("impersonator_original_user");
    sessionStorage.removeItem("impersonator_original_refresh");
    sessionStorage.removeItem("impersonation.returnTo");
    clearPortalAccess();
    // Drop module-level caches so the next login (possibly as a
    // different user) fetches fresh state instead of seeing the
    // previous account's onboarding flag or trial countdown.
    resetOnboardingStatusCache();
    resetSubscriptionStatusCache();

    // Embed-mode routing branches.
    //
    //   1. Partner has `homepage_url` configured → top-level navigation
    //      back to the partner's panel. `window.top.location.href`
    //      (assignment) is allowed cross-origin even though reading
    //      `window.top.location.href` is not — we're only writing.
    //      Setting `window.location.href` would only navigate the
    //      iframe, leaving the user on partner chrome around an empty
    //      iframe — wrong UX.
    //
    //   2. No `homepage_url` configured but the session WAS partner-SSO
    //      → tell the host frame we logged out via postMessage and let
    //      it decide what to do (close the iframe, navigate elsewhere).
    //      Same channel as `noticeai.token_expired` so existing host
    //      listeners are already wired for the lifecycle.
    //
    //   3. Direct UI users (no partner_id, or partner_id but
    //      partner_admin staff) → caller handles `navigate("/login")`.
    //      Return `false` to signal "you still need to redirect".
    if (redirectUrl) {
      try {
        if (typeof window !== "undefined" && window.top) {
          window.top.location.href = redirectUrl;
        }
      } catch {
        // `window.top` access can throw under weird sandboxing — fall
        // back to same-frame nav so we at least leave the auth-cleared
        // state visible somewhere.
        if (typeof window !== "undefined") {
          window.location.href = redirectUrl;
        }
      }
      return true;
    }

    if (wasPartnerSso) {
      try {
        if (typeof window !== "undefined" && window.parent) {
          window.parent.postMessage({ type: "noticeai.logout" }, "*");
        }
      } catch {
        /* swallow — origin restrictions can theoretically block postMessage */
      }
      return true;
    }

    return false;
  };

  const isAdmin = user?.role === "super_admin";
  const isPartnerAdmin = user?.role === "partner_admin";
  const isImpersonating = Boolean(impersonatingUserId);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        loading,
        isReadOnly,
        checkReadOnlyMode,
        isFirstPurchase,
        checkIsFirstPurchase,
        isAdmin,
        isPartnerAdmin,
        impersonatingUserId,
        setImpersonatingUserId,
        isImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
